import { Component } from '@theme/component';

/** A section-local carousel that keeps product media and details in the same state. */
class TimelessFavorites extends Component {
  requiredRefs = ['slideshow'];

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #interval;
  #current = 0;
  #controller;

  connectedCallback() {
    this.#relocate();
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    let start;
    this.refs.slideshow.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse') start = { x: event.clientX, y: event.clientY };
    }, options);
    this.refs.slideshow.addEventListener('pointercancel', () => { start = null; }, options);
    this.refs.slideshow.addEventListener('pointerup', (event) => {
      if (!start) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      start = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        this.suppressClick = true;
        dx < 0 ? this.next() : this.previous();
        setTimeout(() => { this.suppressClick = false; }, 0);
      }
    }, options);
    this.refs.slideshow.addEventListener('click', (event) => {
      if (this.suppressClick) { event.preventDefault(); event.stopPropagation(); }
    }, { ...options, capture: true });
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.refs.slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
      if (index >= 0) { this.#sync(index); this.#stop(); }
    }, options);
    this.addEventListener('focusin', () => this.#stop(), options);
    this.addEventListener('focusout', () => queueMicrotask(() => {
      if (!this.contains(document.activeElement)) this.#play();
    }), options);
    this.#sync(Math.min(this.#current, (this.refs.slides?.length ?? 1) - 1));
    this.#play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#stop();
  }

  next(event) {
    event?.preventDefault();
    this.#sync(this.#current + 1);
  }

  previous(event) {
    event?.preventDefault();
    this.#sync(this.#current - 1);
  }

  /**
   * `content_for 'blocks'` only ever renders each product block once, and it only accepts
   * `closest.*` arguments, so there's no way to ask it for a second, differently-flagged pass to
   * put a block's meta info and feature photo in their own DOM locations directly. Each block
   * instead nests its meta/feature markup inside its own slide (where `block.settings.product`
   * still resolves correctly) and tags it `data-tf-portal`; this moves those nodes into their real
   * containers once, before refs are computed, so `refs.metas`/`refs.features` end up pointing at
   * elements already in the right place.
   */
  #relocate() {
    const metaContainer = this.querySelector('.timeless-favorites__meta');
    const featureContainer = this.querySelector('.timeless-favorites__feature');

    for (const element of this.querySelectorAll('[data-tf-portal="meta"]')) {
      metaContainer?.append(element);
    }

    for (const element of this.querySelectorAll('[data-tf-portal="feature"]')) {
      featureContainer?.append(element);
    }
  }

  /**
   * Points the counter, meta row and feature image at the given slide.
   *
   * @param {number} index - The zero-based index of the active slide.
   */
  #sync(index) {
    const { counter, metas, features, slides } = this.refs;
    if (!slides?.length) return;
    index = (index + slides.length) % slides.length;
    this.#current = index;

    if (counter) counter.textContent = `${index + 1}`.padStart(2, '0');

    for (const group of [slides, metas, features]) {
      group?.forEach((element, position) => {
        const active = position === index;
        element.toggleAttribute('data-active', active);
        element.inert = !active;
        element.setAttribute('aria-hidden', String(!active));
      });
    }
  }

  #play() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const speed = Number.parseFloat(this.getAttribute('autoplay-speed') ?? '');

    if (this.#interval || !Number.isFinite(speed) || speed <= 0) return;
    if ((this.refs.metas?.length ?? 0) < 2) return;

    this.#interval = setInterval(() => {
      if (document.hidden) return;

      this.next();
    }, speed * 1000);
  }

  #stop() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }
}

if (!customElements.get('timeless-favorites')) {
  customElements.define('timeless-favorites', TimelessFavorites);
}
